import {PolymerElement, html} from '@polymer/polymer';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';

class KeliPrompt extends ScopeBinding(HotkeyBehavior(PolymerElement)) {
  static get template() {
    return html`
<style>
  :host {
    outline: none;
    overflow: hidden;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, var(--keli-backdrop-opacity));
    z-index: var(--keli-zindex-prompt);
  }

  #innerPrompt {
    border-radius: 2px;
    box-sizing: border-box;
    width: 800px;
    margin: 5% auto;
    max-width: 80%;
    max-height: 80%;
    display: flex;
    flex-direction: column;
    background-color: var(--keli-background-color);
    box-shadow: var(--keli-shadow-z3);
  }

  #content {
    overflow: auto;
    padding: 10px 20px;
  }

  keli-markdown#text {
    padding: 0 !important;
  }

  #actions {
    box-sizing: border-box;
    min-height: 50px;
    padding: 5px 20px;
    text-align: right;
  }

  #actions keli-button {
    margin-right: 10px;
    color: var(--keli-prompt-action-color);
  }

  h2#title {
    font-size: 1.3em;
    font-weight: 700;
    text-align: left;
  }

  #controls {
    display: flex;
    position: relative;
    padding: 10px 10px 20px 10px;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-content: space-between;
    overflow-x: hidden;
  }

  #controls .input-field {
    flex: 1 0 170px;
    margin: 0 10px;
  }

  #controls keli-textarea {
    max-width: none !important;
    flex: 1 1 100% !important;
  }
</style>

<keli-prompt-controller id="controller"></keli-prompt-controller>

<div id="innerPrompt">
  <div id="content">
    <h2 id="title">[[caption]]</h2>
    <keli-markdown id="text" ro></keli-markdown>
    <div id="controls"></div>
  </div>
  <div id="actions"></div>
</div>`;
  }

  static get is() {
    return 'keli-prompt';
  }

  static get properties() {
    return {
      activeElement: Object,
      caption: String,
      closing: Boolean,
      text: {type: String, observer: 'textChanged'},
      panel: Object
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._ensureAttribute('tabindex', '0');
    this.addEventListener('panelElementCreated', this.handlePanelElementCreated);
    this.addEventListener('setPanelActiveElement', this.handleSetPanelActiveElement);
    this.addEventListener('registerPanelHotkey', this.handleRegisterPanelHotkey);

    afterNextRender(this, () => {
      this.focusActiveControl();
    });

    this.AdjacentElement = new AdjacentElement({
      'this': this.$.innerPrompt,
      'selector': '#content .input-field, #content keli-button, #actions keli-button:last-of-type',
      'wrap': true,
      'defaultCallback': (newElement) => {
        afterNextRender(this, () => {
          if (newElement) {
            newElement.dispatchEvent(new CustomEvent('focusElement', {detail: {element: newElement}}));
          } else {
            prompt.focus();
          }
        });
      }
    });

    this.AdjacentElementButtons = new AdjacentElement({
      'this': this.$.actions,
      'selector': 'keli-button',
      'wrap': true,
      'defaultCallback': function (newElement) {
        if (newElement) {
          newElement.dispatchEvent(new CustomEvent('focusElement', {detail: {element: newElement}}));
        }
      }
    });

    this.registerHotkey(KeliInterface.Hotkeys.prompt.close, {
      down: () => {
        this.close();
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.prompt.submit, {
      down: () => {
        this.dispatchEvent(new CustomEvent('promptTriggerDefaultAction'));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.prompt.next, {
      down: () => {
        if (this.AdjacentElement) {
          this.AdjacentElement.next(this.activeElement);
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.prompt.previous, {
      down: () => {
        if (this.AdjacentElement) {
          this.AdjacentElement.previous(this.activeElement);
        }
      }
    });

    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.registerHotkey(KeliInterface.Hotkeys.prompt.nextButton, {
      down: event => {
        if (this.AdjacentElementButtons && this.activeElement.tagName.toLowerCase() === 'keli-button') {
          event.preventDefault();
          event.stopPropagation();
          this.AdjacentElementButtons.next(this.activeElement);
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.prompt.previousButton, {
      down: event => {
        if (this.AdjacentElementButtons && this.activeElement.tagName.toLowerCase() === 'keli-button') {
          event.preventDefault();
          event.stopPropagation();
          this.AdjacentElementButtons.previous(this.activeElement);
        }
      }
    }, 'raw-listener');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('panelElementCreated', this.handlePanelElementCreated);
    this.removeEventListener('setPanelActiveElement', this.handleSetPanelActiveElement);
    this.removeEventListener('registerPanelHotkey', this.handleRegisterPanelHotkey);

    this.activeElement = null;
    this.panel = null;
    if (this.AdjacentElement) {
      this.AdjacentElement.destroy();
      this.AdjacentElement = null;
    }
    if (this.AdjacentElementButtons) {
      this.AdjacentElementButtons.destroy();
      this.AdjacentElementButtons = null;
    }
  }

  async keliReady(scope) {
    super.keliReady && await super.keliReady(scope);
    await this.$.controller.elementReady(this);
  }

  textChanged(newValue) {
    this.$.text.displayValue = newValue;
  }

  handleSetPanelActiveElement(event) {
    event.stopPropagation();
    if (this.AdjacentElement) {
      this.AdjacentElement.element = event.detail.element;
    }
    this.activeElement = event.detail.element;
    this.resetHotkeyState();

    // Manually focus buttons to fix a bug when tabbing from lookups to the primary button.
    if (event.detail.element.tagName === 'KELI-BUTTON') {
      event.detail.element.focus();
    }
  }

  handleRegisterPanelHotkey(event) {
    event.stopPropagation();
    this.registerHotkey({
      'keys': event.detail.keys,
      'this': event.detail.scope || undefined,
      'is_unordered': true,
      'prevent_repeat': true
    }, {
      down: () => {
        if (event.detail.scope) {
          event.detail.scope.activate(event);
        }
      }
    });
  }

  focusActiveControl() {
    if (!this.AdjacentElement || KeliInterface.isTouchDevice) {
      return;
    }
    if (this.panel && !this.panel.active) {
      return;
    }
    this.AdjacentElement.first();
  }

  close() {
    this.closing = true;
    this.dispatchEvent(new CustomEvent('globalFocusReleased', {bubbles: true, composed: true}));
    this.remove();
  }
}

customElements.define(KeliPrompt.is, KeliPrompt);
export {KeliPrompt};
